import { treePathOfV1Path, type ProjectTree } from '@gotti/sandbox-core';

/**
 * Pure helpers for the Tests panel's "click a test / assertion → open the
 * spec and jump to that line" behaviour. Kept side-effect-free so the
 * line-resolution logic is unit-testable without React or Monaco.
 */

/**
 * Strip a single leading slash so a frame path (`/client/__tests__/Game.spec.ts`)
 * and the editor's sandbox-relative path (`client/__tests__/Game.spec.ts`)
 * compare equal.
 */
function stripLeadingSlash(p: string): string {
  return p.startsWith('/') ? p.slice(1) : p;
}

/**
 * Normalize a path for comparison: drop a single leading slash AND a trailing
 * `.ts`/`.tsx`. The spec path the panel passes always carries `.ts`, whereas a
 * stack/sourceURL frame's path may or may not — comparing the extension-less
 * forms makes the match robust to that convention drift.
 */
function normalizePathForCompare(p: string): string {
  return stripLeadingSlash(p).replace(/\.tsx?$/, '');
}

/**
 * A source line that REGISTERS a test/suite — `it(`, `test(`, `describe(`, or a
 * `defineSandboxTest` object's `label:`/`title:`/`name:`. Used to prefer the
 * real declaration over an incidental quoted mention of the same name.
 */
const DECLARATION_KEYWORD = /\b(describe|it|test|label|title|name)\s*[:(]/;

/** True for a line whose trimmed content is a `//`, `*`, or `/*` comment. */
function isCommentLine(line: string): boolean {
  const t = line.trimStart();
  return t.startsWith('//') || t.startsWith('*') || t.startsWith('/*');
}

/**
 * Resolve the 1-based source line where a test or suite is DECLARED, by
 * locating its name. Test/suite names appear verbatim in the spec source —
 * as a `defineSandboxTest` `title:`/`label:`, or as a factory call's first
 * string argument (e.g. `movementTest('pressing ArrowRight …', …)`).
 *
 * Resolution is layered, most-specific first, so an incidental mention never
 * beats the real declaration:
 *   1. a non-comment line that QUOTES the name AND carries a declaration
 *      keyword (`it(`/`test(`/`describe(`/`label:`/`title:`/`name:`);
 *   2. a non-comment line that quotes the name (covers factory calls that have
 *      no literal keyword);
 *   3. a non-comment line containing the raw name;
 *   4. any line at all (incl. comments) — a last resort, better than nothing.
 * Returns `undefined` only when the name can't be located anywhere (caller then
 * just opens the file at the top).
 */
export function declarationLine(code: string, name: string): number | undefined {
  if (!name) return undefined;
  const lines = code.split('\n');
  const quoted = [`'${name}'`, `"${name}"`, '`' + name + '`'];
  const isQuoted = (line: string) => quoted.some(q => line.includes(q));

  for (let i = 0; i < lines.length; i++) {
    if (!isCommentLine(lines[i]) && isQuoted(lines[i]) && DECLARATION_KEYWORD.test(lines[i])) {
      return i + 1;
    }
  }
  for (let i = 0; i < lines.length; i++) {
    if (!isCommentLine(lines[i]) && isQuoted(lines[i])) return i + 1;
  }
  for (let i = 0; i < lines.length; i++) {
    if (!isCommentLine(lines[i]) && lines[i].includes(name)) return i + 1;
  }
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(name)) return i + 1;
  }
  return undefined;
}

/**
 * Resolve the 1-based source line of a failing assertion from a test's
 * `errorFrames` — the normalized `path:line:col` call-sites parsed from the
 * thrown error's stack (see `@gotti/sandbox-test` `parseErrorFrames`). Returns
 * the line of the FIRST frame that points into `specPath` (the top-of-stack
 * frame inside the spec is the failing `expect(...)` call-site). Returns
 * `undefined` when no frame is in the spec file.
 */
export function assertionLineFromFrames(
  errorFrames: string[] | undefined,
  specPath: string,
): number | undefined {
  if (!errorFrames || errorFrames.length === 0) return undefined;
  const target = normalizePathForCompare(specPath);
  for (const frame of errorFrames) {
    const m = /^(.*):(\d+):(\d+)$/.exec(frame);
    if (!m) continue;
    if (normalizePathForCompare(m[1]) === target) return Number(m[2]);
  }
  return undefined;
}

/**
 * A file's contents in the project tree, by the path a test result or discovery names it: an AI v1 path
 * (`client/__tests__/Game.spec.ts`, `__tests__/x.spec.ts` — under `src/`), or a tree path (`sandbox.playground.ts`). A
 * leading slash is tolerated, and a path without its `.ts` is tried with it. `undefined` when the project has no such file.
 */
export function getFileCode(project: ProjectTree, path: string): string | undefined {
  const bare = stripLeadingSlash(path);
  const candidates = /\.[A-Za-z0-9]+$/.test(bare) && !/\.(spec|test)$/.test(bare) ? [bare] : [bare, `${bare}.ts`];
  for (const candidate of candidates) {
    const treePath = treePathOfV1Path(candidate);
    if (treePath !== null && Object.hasOwn(project.files, treePath)) return project.files[treePath];
    if (Object.hasOwn(project.files, candidate)) return project.files[candidate];
  }
  return undefined;
}
